import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ReactComponent as DownIcon } from '../../../../assets/icons/down-icon.svg';
import Popup from '../../../../components/Popup/Popup';
import './InviteModule.css';

function InviteModule({ 
    isOpen, 
    onClose, 
    org, 
    user, 
    onInviteSuccess,
    setupSteps,
    setSetupSteps 
}) {
    const [emailInput, setEmailInput] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [inviteRole, setInviteRole] = useState('admin');
    const [inviteRoleSelectionOpen, setInviteRoleSelectionOpen] = useState(false);
    
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);
    const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
    const [shareAllWorkspaces, setShareAllWorkspaces] = useState(true);
    const canInvite = emailInput.length > 0 && (inviteRole === 'admin' || shareAllWorkspaces || selectedWorkspaces.length > 0);
    const selectedWorkspaceCount = selectedWorkspaces.length;

    useEffect(() => {
        if (inviteRole === 'member' && isOpen) {
            fetchWorkspaces();
        }
    }, [inviteRole, isOpen]);

    const fetchWorkspaces = async () => {
        setLoadingWorkspaces(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-workspaces/${user.id}/${org.id}`, { withCredentials: true });
            setWorkspaces(response.data);
            // Initialize with all workspaces selected when share all is ON
            if (response.data.length > 0) {
                setSelectedWorkspaces(response.data.map(w => w.id));
            }
        } catch (error) {
            console.error('Error fetching workspaces:', error);
        } finally {
            setLoadingWorkspaces(false);
        }
    };

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleWorkspaceToggle = (workspaceId) => {
        const wasSelected = selectedWorkspaces.includes(workspaceId);
        const updatedWorkspaces = wasSelected
            ? selectedWorkspaces.filter(id => id !== workspaceId)
            : [...selectedWorkspaces, workspaceId];
        
        setSelectedWorkspaces(updatedWorkspaces);
        
        // Only turn off the toggle if a workspace was deselected
        if (wasSelected) {
            setShareAllWorkspaces(false);
        }
        // Don't automatically turn on the toggle when all workspaces are selected
    };

    const handleShareAllWorkspacesToggle = () => {
        const newShareAll = !shareAllWorkspaces;
        setShareAllWorkspaces(newShareAll);
        
        if (newShareAll) {
            // Switching to share all - select all workspaces
            setSelectedWorkspaces(workspaces.map(w => w.id));
        } else {
            // Switching to limited - keep current selections or select all if none selected
            if (selectedWorkspaces.length === 0) {
                setSelectedWorkspaces(workspaces.map(w => w.id));
            }
        }
    };

    const handleInviteClick = () => {
        if (user?.orgRole !== 'admin') {
            setErrorMessage('Only admins can invite new members.');
            return;
        }

        const inputEmails = emailInput
            .split(/[\s,]+/)
            .map((email) => email.trim())
            .filter((email) => email.length > 0);

        const invalidEmails = inputEmails.filter((email) => !validateEmail(email));

        if (invalidEmails.length > 0 || inputEmails.length === 0) {
            setErrorMessage(`Invalid email(s): ${invalidEmails.join(', ')}`);
            return;
        }

        // Check for self-invitation
        const selfInvite = inputEmails.find(email => email.toLowerCase() === user.email.toLowerCase());
        if (selfInvite) {
            setErrorMessage('You cannot invite yourself.');
            return;
        }

        // For admins, invite to all workspaces. For members, use selected workspaces
        const workspaceIds = inviteRole === 'admin' ? workspaces.map(w => w.id) : selectedWorkspaces;

        // Check if member has selected at least one workspace (only for member role and limited access)
        if (inviteRole === 'member' && !shareAllWorkspaces && selectedWorkspaces.length === 0) {
            setErrorMessage('Please select at least one workspace to invite the member to.');
            return;
        }

        // Set access level: 'full' if admin OR share all workspaces is enabled, 'limited' otherwise
        const accessLevel = (inviteRole === 'admin' || shareAllWorkspaces) ? 'full' : 'limited';

        setErrorMessage('');
        axios
            .post(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/invite-members`, {
                orgId: org.id,
                orgName: org.name,
                emails: inputEmails,
                senderName: user.name,
                orgRole: inviteRole,
                accessLevel: accessLevel,
                workspaceIds: workspaceIds
            }, { withCredentials: true })
            .then((response) => {
                console.log('Invitations sent.');
                setEmailInput('');
                updateSetupProgress();
                onInviteSuccess();
                onClose();
            })
            .catch((error) => {
                console.error('Error sending invitations', error);
                setErrorMessage('Failed to send invitations. Please try again.');
            });
    };

    const updateSetupProgress = async () => {
        // try {
        //     const stepId = 5;
        //     if (org?.showSetup && !setupSteps.find(step => step.id === stepId).completed) {
        //         await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/update-setup-progress`, {
        //             orgId: org.id,
        //             figureToUpdate: 'inviteTeamMember',
        //             completed: true
        //         }, { withCredentials: true });
        //         setSetupSteps(prevSteps => prevSteps.map(step => 
        //             step.id === stepId ? { ...step, completed: true } : step
        //         ));
        //     }
        // } catch (error) {
        //     console.error('Error updating setup progress:', error);
        // }
    };

    const renderInviteRoleSelection = () => {
        return (
            <div className="selection-menu">
                <div 
                    className="status-option" 
                    data-role="admin"
                    onClick={() => {
                        setInviteRole('admin');
                        setInviteRoleSelectionOpen(false);
                    }}
                >
                    Admin
                </div>
                <div 
                    className="status-option" 
                    data-role="member"
                    onClick={() => {
                        setInviteRole('member');
                        setInviteRoleSelectionOpen(false);
                    }}
                >
                    Member
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <Popup content={
            <div className="invite-section">
                <div className="invite-header">
                    <div className="invite-header-copy">
                        <span className="invite-kicker">Team Access</span>
                        <h3>Invite New Members</h3>
                        <p>
                            Send one or more invitations and choose the level of access they should receive.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="invite-close-button"
                        onClick={onClose}
                        aria-label="Close invite dialog"
                    >
                        X
                    </button>
                </div>

                <div className="invite-form-grid">
                    <div className="invite-field invite-field-email">
                        <label htmlFor="invite-emails">Email addresses</label>
                        <div className="invite-input">
                            <input
                                id="invite-emails"
                                type="text"
                                placeholder="name@company.com, teammate@company.com"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                            />
                        </div>
                        <p className="invite-helper-text">Separate multiple email addresses with commas or spaces.</p>
                    </div>

                    <div className="invite-field invite-field-role">
                        <label htmlFor="invite-role-button">Role</label>
                        <div className={`member-role-selection ${inviteRole}`}>
                            <button
                                id="invite-role-button"
                                type="button"
                                className="status"
                                onClick={() => setInviteRoleSelectionOpen(!inviteRoleSelectionOpen)}
                            >
                                <div className="status-text">{inviteRole.charAt(0).toUpperCase() + inviteRole.slice(1)}</div>
                                <DownIcon className="down-icon" />
                            </button>
                            {inviteRoleSelectionOpen && renderInviteRoleSelection()}
                        </div>
                        <p className="invite-helper-text">
                            {inviteRole === 'admin'
                                ? 'Admins can manage members and access every workspace.'
                                : 'Members can be limited to specific workspaces.'}
                        </p>
                    </div>
                </div>

                <div className="role-info">
                    {inviteRole === 'admin' ? (
                        <div className="role-info-card role-info-admin">
                            <span className="role-info-badge">Full access</span>
                            <span className="role-info-text">Admins are always invited to all current and future workspaces.</span>
                        </div>
                    ) : (
                        <div className="role-info-card role-info-member">
                            <div className="workspace-selection-header">
                                <div className="workspace-selection-copy">
                                    <div className="workspace-selection-title">Workspace access</div>
                                    <div className="workspace-selection-description">
                                        Control whether this member gets access to all workspaces or only a selected set.
                                    </div>
                                </div>
                                <div className="share-all-toggle-container">
                                    <span className="toggle-text">All current and future</span>
                                    <label className="toggle-label">
                                        <input
                                            type="checkbox"
                                            className="toggle-input"
                                            checked={shareAllWorkspaces}
                                            onChange={handleShareAllWorkspacesToggle}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                            </div>
                            {loadingWorkspaces ? (
                                <div className="workspace-loading">
                                    <span>Loading workspaces...</span>
                                </div>
                            ) : (
                                <div className="workspace-selection-container">
                                    {!shareAllWorkspaces && (
                                        <div className="workspace-selection-meta">
                                            {selectedWorkspaceCount} workspace{selectedWorkspaceCount === 1 ? '' : 's'} selected
                                        </div>
                                    )}
                                    <div className="workspace-list">
                                        {workspaces.map(workspace => (
                                            <div key={workspace.id} className="workspace-checkbox-container">
                                                <input
                                                    type="checkbox"
                                                    id={`workspace-${workspace.id}`}
                                                    className="workspace-checkbox"
                                                    checked={selectedWorkspaces.includes(workspace.id)}
                                                    onChange={() => handleWorkspaceToggle(workspace.id)}
                                                    disabled={shareAllWorkspaces}
                                                />
                                                <label htmlFor={`workspace-${workspace.id}`} className="role-info-text">
                                                    {workspace.name}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="invite-button-container">
                    <button
                        className="cancel-button"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        className={`invite-button ${canInvite ? 'active' : ''}`}
                        onClick={handleInviteClick}
                        disabled={!canInvite}
                    >
                        Invite
                    </button>
                </div>
                {errorMessage && <div className="error-message">{errorMessage}</div>}
            </div>
        } onClose={onClose} />
    );
}

export default InviteModule; 